import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, SignOut, User, Heart, CheckCircle, Clock, ChatCircleDots, ArrowBendDownRight, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { SITE } from "@/data/site";
import SEO from "@/components/SEO";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const wishlist = useWishlist();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: "/account" } });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/my/messages").then((r) => setInquiries(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading || !user) return <div className="container-px py-32 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" /></div>;

  return (
    <>
      <SEO title="My Account" description="Manage your orders, inquiries and account." path="/account" />
      <div className="container-px py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><User size={28} weight="bold" /></span>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Hi, {user.name.split(" ")[0]} 👋</h1>
              <p className="text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="btn-outline !py-2.5 text-sm" data-testid="logout-btn"><SignOut size={18} /> Sign out</button>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {[[Package, orders.length, "Orders"], [Heart, wishlist.count, "Wishlist items"], [ChatCircleDots, inquiries.length, "Inquiries"]].map(([I, n, l], i) => (
            <div key={i} className="rounded-3xl border border-border bg-card p-6">
              <I size={28} className="text-primary" weight="bold" />
              <div className="mt-3 text-3xl font-black">{n}</div>
              <div className="text-sm text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-black tracking-tight">Order history</h2>
          {orders.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-border bg-card p-10 text-center">
              <Package size={48} className="mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-muted-foreground">You haven't placed any orders yet.</p>
              <Link to="/shop" className="btn-primary mt-5">Start shopping</Link>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="rounded-3xl border border-border bg-card p-5" data-testid={`order-${o.order_number || o.order_id?.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="font-mono text-sm font-semibold">#{o.order_number || o.order_id?.slice(0, 8)}</span>
                      <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${o.payment_status === "paid" ? "bg-green-500/10 text-green-600" : "bg-amber-500/10 text-amber-600"}`}>
                      {o.payment_status === "paid" ? <CheckCircle size={13} weight="fill" /> : <Clock size={13} />}
                      {o.payment_status === "paid" ? "Paid" : "Pending Payment — Awaiting Bank Transfer"}
                    </span>
                  </div>
                  {o.payment_status !== "paid" && (
                    <p className="mt-2 text-xs text-muted-foreground">Pay by bank transfer using reference <span className="font-mono font-bold text-primary">#{o.order_number || o.order_id?.slice(0, 8)}</span> — your bike is reserved for 48 hours.</p>
                  )}
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                    <div className="flex -space-x-3">
                      {o.items?.slice(0, 4).map((it) => <img key={it.slug} src={it.thumbnail} alt={it.name} className="h-12 w-12 rounded-full border-2 border-card object-cover" loading="lazy" />)}
                    </div>
                    <span className="text-lg font-black">{money(o.amount * (o.fx_rate || 1), o.currency || "GBP")}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="account-contact-cta">
          <h2 className="text-2xl font-black tracking-tight">Need help with an order?</h2>
          <p className="mt-2 text-sm text-muted-foreground">Our experts reply fast — reach us on WhatsApp or by email.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="btn-primary !py-2.5 text-sm" data-testid="account-whatsapp-btn"><WhatsappLogo size={16} weight="fill" /> WhatsApp us</a>
            <a href={`mailto:${SITE.email}`} className="btn-outline !py-2.5 text-sm" data-testid="account-email-btn"><EnvelopeSimple size={16} /> {SITE.email}</a>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-black tracking-tight">My inquiries</h2>
          {inquiries.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-border bg-card p-8 text-center" data-testid="no-inquiries">
              <ChatCircleDots size={40} className="mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">No inquiries yet. Questions about a bike or order? <Link to="/contact" className="font-semibold text-primary">Contact us</Link> and our replies will appear here.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="my-inquiries">
              {inquiries.map((m) => (
                <div key={m.id} className="rounded-3xl border border-border bg-card p-5" data-testid={`inquiry-${m.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-bold">{m.subject || "General inquiry"}</span>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${m.status === "replied" ? "bg-green-500/10 text-green-600" : "bg-amber-500/10 text-amber-600"}`}>{m.status === "replied" ? "Replied" : "Awaiting reply"}</span>
                      <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString("en-GB")}</span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{m.message}</p>
                  {m.reply && (
                    <div className="mt-3 flex gap-3 rounded-2xl bg-primary/5 border border-primary/15 p-4" data-testid={`inquiry-reply-${m.id}`}>
                      <ArrowBendDownRight size={18} className="mt-0.5 shrink-0 text-primary" />
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-primary">Reply from Ebikes&Gear {m.replied_at && `· ${new Date(m.replied_at).toLocaleDateString("en-GB")}`}</p>
                        <p className="mt-1 text-sm">{m.reply}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
